import { ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { PaginationMeta, ReviewRun, ReviewRunFilters } from '@/types/models'

// Singleton, following useGithubRepositorySettings.ts's shape: shared state so
// the review history view retains its list/pagination across navigation
// within a session.
const reviewRuns = ref<ReviewRun[]>([])
const meta = ref<PaginationMeta | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// Filter/page changes fire a new fetch per change (see ReviewHistoryView's
// repo text input); this guards against an older request's response landing
// after a newer one's and clobbering it.
const requestGuard = createRequestGuard()

export function useReviewRuns() {
  async function fetchReviewRuns(installationId: string, filters: ReviewRunFilters = {}): Promise<void> {
    if (!installationId) return
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load review history.',
      async () => {
        const page = await githubApi.listReviewRuns(installationId, filters)
        if (!requestGuard.isCurrent(token)) return
        reviewRuns.value = page.data
        meta.value = page.meta
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  // Called on logout so the next session's review history doesn't briefly
  // show the previous user's data before its own fetch resolves, and so a
  // late response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    reviewRuns.value = []
    meta.value = null
    loading.value = false
    error.value = null
  }

  return {
    reviewRuns,
    meta,
    loading,
    error,
    fetchReviewRuns,
    reset,
  }
}
